import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ClipboardList } from 'lucide-react';
import type { PublicFormDue } from '@rx-manager/shared';
import { useI18n } from '../../lib/i18n';
import { Button, IconTile } from '../../components/ui';
import { rememberSession } from './form-session';

/**
 * "Before your visit": the forms a booking asks for, on the done screen and the
 * manage page (SFM-D11). Right after booking, the done screen hands over a
 * session so the patient is not asked the date of birth they just typed.
 *
 * Track SFM rev 2: a form answered inside the booking is listed as "Sent with
 * your booking", with no button (SFM-D29); one skipped there still opens from
 * here. Forms still to fill in come first.
 */
export function FormsDue({ forms }: { forms: PublicFormDue[] }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  if (forms.length === 0) return null;
  const isOpen = (form: PublicFormDue) =>
    (form.state === 'due' || form.state === 'in_progress') && Boolean(form.token);
  const open = forms.filter(isOpen);
  const ordered = [...open, ...forms.filter((form) => !isOpen(form))];

  return (
    <section
      aria-labelledby="forms-due-heading"
      className="space-y-4 rounded-xl border border-primary/25 bg-accent/50 p-4 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <IconTile icon={ClipboardList} className="bg-card" />
        <div className="space-y-1">
          <h2 id="forms-due-heading" className="text-lg font-semibold text-heading">
            {open.length > 0 ? t('form.dueHeading') : t('form.formsHeading')}
          </h2>
          {open.length > 0 && (
            <p className="text-base text-foreground">
              {open.length > 1 ? t('form.dueIntroMany') : t('form.dueIntro')}
            </p>
          )}
        </div>
      </div>
      <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
        {ordered.map((form) => {
          const title = locale === 'fr' && form.title.fr ? form.title.fr : form.title.en;
          const token = isOpen(form) ? form.token : undefined;
          return (
            <li
              key={form.formId}
              className="flex flex-wrap items-center justify-between gap-3 p-4"
            >
              <div className="min-w-0">
                <p className="text-base font-semibold text-heading">{title}</p>
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  {form.state === 'submitted' && (
                    <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />
                  )}
                  {form.state === 'submitted'
                    ? form.sentWithBooking
                      ? t('form.sentWithBooking')
                      : t('form.dueSent')
                    : form.state === 'closed'
                      ? t('form.dueClosed')
                      : form.required
                        ? t('form.dueRequired')
                        : t('form.dueOptional')}
                </p>
              </div>
              {token && (
                <Button
                  onClick={() => {
                    if (form.session) rememberSession(token, form.session);
                    navigate(`/f/${token}`);
                  }}
                >
                  {form.state === 'in_progress' ? t('form.carryOn') : t('form.fillIn')}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
